import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import { useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import PageShell from "components/patch/PageShell";

const links = [
  {
    title: "Pay Items",
    route: "/setup/pay-items",
    icon: <PaymentsRoundedIcon fontSize="small" />,
    badge: "Payroll",
  },
  {
    title: "Grade Levels",
    route: "/setup/grade-levels",
    icon: <AccountTreeRoundedIcon fontSize="small" />,
    badge: "Structure",
  },
  {
    title: "Cadres",
    route: "/setup/cadres",
    icon: <BadgeRoundedIcon fontSize="small" />,
    badge: "Structure",
  },
  {
    title: "Staff Payment Details",
    route: "/setup/staffdetails",
    icon: <PaymentsRoundedIcon fontSize="small" />,
    badge: "Staff",
  },
  {
    title: "Staff Groups",
    route: "/setup/staff-group",
    icon: <BadgeRoundedIcon fontSize="small" />,
    badge: "Staff",
  },
  {
    title: "Salary Structures",
    route: "/setup/salary-structures",
    icon: <AccountTreeRoundedIcon fontSize="small" />,
    badge: "Payroll",
  },
  {
    title: "Mapped Salary Structure",
    route: "/setup/map-salary-structure",
    icon: <AccountTreeRoundedIcon fontSize="small" />,
    badge: "Payroll",
  },
  {
    title: "Designations",
    route: "/setup/designations",
    icon: <BadgeRoundedIcon fontSize="small" />,
    badge: "Staff",
  },
  {
    title: "Earnings",
    route: "/setup/earnings",
    icon: <PaymentsRoundedIcon fontSize="small" />,
    badge: "Payroll",
  },
  {
    title: "Deductions",
    route: "/setup/deductions",
    icon: <PaymentsRoundedIcon fontSize="small" />,
    badge: "Payroll",
  },
  {
    title: "Irregular Allowances",
    route: "/setup/irregularallowances",
    icon: <PaymentsRoundedIcon fontSize="small" />,
    badge: "Rules",
  },
  {
    title: "Staff Categories",
    route: "/setup/staff-categories",
    icon: <BadgeRoundedIcon fontSize="small" />,
    badge: "Staff",
  },
  {
    title: "Arrears Upload",
    route: "/setup/arrearsupload",
    icon: <SettingsRoundedIcon fontSize="small" />,
    badge: "Upload",
  },
];

export default function OrgSetup() {
  const navigate = useNavigate();

  return (
    <PageShell
      title="Organization Setup"
      subtitle="Configure payroll master data, salary structures, staff classifications, and core setup modules."
    >
      <MDBox mt={1}>
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: "24px",
            background: "#fff",
            border: "1px solid rgba(200, 30, 30, 0.08)",
            boxShadow: "0 12px 28px rgba(16, 40, 79, 0.04)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "0.74rem",
                  fontWeight: 800,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "#c81e1e",
                  mb: 1,
                }}
              >
                Payroll Configuration
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "1.5rem", md: "1.85rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  color: "#10284f",
                  mb: 1,
                }}
              >
                Setup workspace
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.95rem",
                  lineHeight: 1.75,
                  color: "#62708a",
                  maxWidth: 760,
                }}
              >
                Open the core modules used to define payroll structures, earnings, deductions, staff
                classifications, and supporting setup records.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label="Structures"
                sx={{
                  height: 34,
                  fontWeight: 700,
                  color: "#b42318",
                  background: "#fdf0f0",
                  borderRadius: "999px",
                }}
              />
              <Chip
                label="Payroll Rules"
                sx={{
                  height: 34,
                  fontWeight: 700,
                  color: "#1d4ed8",
                  background: "#eff6ff",
                  borderRadius: "999px",
                }}
              />
              <Chip
                label="Master Data"
                sx={{
                  height: 34,
                  fontWeight: 700,
                  color: "#027a48",
                  background: "#ecfdf3",
                  borderRadius: "999px",
                }}
              />
            </Stack>
          </Stack>
        </Box>

        <Grid container spacing={2.5} alignItems="stretch">
          {links.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.route}>
              <Card
                sx={{
                  height: "100%",
                  minHeight: 220,
                  borderRadius: "22px",
                  border: "1px solid rgba(19, 34, 56, 0.08)",
                  background: "#fff",
                  boxShadow: "0 10px 24px rgba(16, 40, 79, 0.04)",
                  overflow: "hidden",
                  transition:
                    "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 18px 36px rgba(16, 40, 79, 0.08)",
                    borderColor: "rgba(200, 30, 30, 0.12)",
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(item.route)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "stretch",
                  }}
                >
                  <CardContent
                    sx={{
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ mb: 2.5 }}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: "16px",
                          background: "linear-gradient(135deg, #d61f1f 0%, #b91c1c 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          boxShadow: "0 10px 20px rgba(200, 30, 30, 0.16)",
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: "#64748b",
                          background: "#f8fafc",
                          borderRadius: "999px",
                        }}
                      />
                    </Stack>

                    <Typography
                      sx={{
                        fontSize: "1.02rem",
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        color: "#10284f",
                        mb: 1.25,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        lineHeight: 1.7,
                        color: "#62708a",
                        mb: 2.5,
                        flexGrow: 1,
                      }}
                    >
                      Open and manage this payroll setup module.
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{
                        color: "#c81e1e",
                        fontWeight: 700,
                        fontSize: "0.87rem",
                      }}
                    >
                      <span>Open module</span>
                      <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    </PageShell>
  );
}
